import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, Check, Settings, Users, FolderOpen, Zap, Shield, BarChart3, Headphones, X, CheckCircle2, MinusCircle, Edit2 } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface PlanLimits {
  maxStaff: number;
  maxProjects: number;
  maxBillExtractionsPerMonth: number;
  maxIntegrations: number;
  maxAuditLogRetentionDays: number;
}

interface PlanFeatures {
  auditLogs: boolean;
  seatManagement: boolean;
  aiUsageAnalytics: boolean;
  prioritySupport: boolean;
}

interface PlanPricing {
  basePrice: number;
  pricePerSeat: number;
  billingInterval: 'day' | 'month' | 'year';
}

interface PlanConfig {
  planName: 'free' | 'premium';
  displayName: string;
  description: string;
  limits: PlanLimits;
  pricing: PlanPricing;
  features: PlanFeatures;
}

interface FeatureRow {
  category?: string;
  label: string;
  field: string;
  type: 'limit' | 'feature' | 'pricing' | 'text';
  icon?: any;
  suffix?: string;
}

export const SubscriptionPlanConfig: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [freePlan, setFreePlan] = useState<PlanConfig | null>(null);
  const [premiumPlan, setPremiumPlan] = useState<PlanConfig | null>(null);
  const [editingCell, setEditingCell] = useState<{ plan: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState<any>('');

  const isSystemAdmin = user?.role === 'System Administrator';

  const featureRows: FeatureRow[] = [
    // Plan Info
    { category: t('admin.subscriptionPlanConfig.planDescription'), label: t('admin.subscriptionPlanConfig.displayName'), field: 'displayName', type: 'text' },
    { label: t('admin.subscriptionPlanConfig.planDescription'), field: 'description', type: 'text' },
    
    // Usage Limits
    { category: t('admin.subscriptionPlanConfig.limits'), label: t('admin.subscriptionPlanConfig.maxStaff'), field: 'limits.maxStaff', type: 'limit', icon: Users },
    { label: t('admin.subscriptionPlanConfig.maxProjects'), field: 'limits.maxProjects', type: 'limit', icon: FolderOpen },
    { label: t('admin.subscriptionPlanConfig.maxBillExtractions'), field: 'limits.maxBillExtractionsPerMonth', type: 'limit' },
    { label: t('admin.subscriptionPlanConfig.maxIntegrations'), field: 'limits.maxIntegrations', type: 'limit' },
    { label: t('admin.subscriptionPlanConfig.maxAuditRetention'), field: 'limits.maxAuditLogRetentionDays', type: 'limit', suffix: ' days' },
    
    // Pricing
    { category: t('admin.subscriptionPlanConfig.pricing'), label: t('admin.subscriptionPlanConfig.pricePerSeat'), field: 'pricing.pricePerSeat', type: 'pricing', suffix: ' ¢' },
    { label: t('admin.subscriptionPlanConfig.billingInterval'), field: 'pricing.billingInterval', type: 'text' },
    
    // Features
    { category: t('admin.subscriptionPlanConfig.features'), label: t('admin.subscriptionPlanConfig.enableAuditLogs'), field: 'features.auditLogs', type: 'feature' },
    { label: t('admin.subscriptionPlanConfig.enableSeatManagement'), field: 'features.seatManagement', type: 'feature' },
    { label: t('admin.subscriptionPlanConfig.enableAIAnalytics'), field: 'features.aiUsageAnalytics', type: 'feature', icon: BarChart3 },
    { label: t('admin.subscriptionPlanConfig.enablePrioritySupport'), field: 'features.prioritySupport', type: 'feature', icon: Headphones },
  ];

  useEffect(() => {
    if (isSystemAdmin) {
      fetchPlanConfigs();
    }
  }, [isSystemAdmin]);

  const fetchPlanConfigs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/plan-config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || t('admin.subscriptionPlanConfig.loadingFailed'));
      }

      const configs = data.data;
      const free = configs.find((c: PlanConfig) => c.planName === 'free');
      const premium = configs.find((c: PlanConfig) => c.planName === 'premium');
      
      if (free) setFreePlan(free);
      if (premium) setPremiumPlan(premium);
      
    } catch (err) {
      console.error('Failed to fetch plan configs:', err);
      setError(err instanceof Error ? err.message : t('admin.subscriptionPlanConfig.loadingFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((curr, key) => curr?.[key], obj);
  };

  const setNestedValue = (obj: any, path: string, value: any): any => {
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    const target = keys.reduce((curr, key) => {
      if (!curr[key]) curr[key] = {};
      return curr[key];
    }, obj);
    target[lastKey] = value;
    return obj;
  };

  const startEdit = (planName: string, field: string) => {
    const plan = planName === 'free' ? freePlan : premiumPlan;
    if (!plan) return;
    
    const value = getNestedValue(plan, field);
    setEditValue(value);
    setEditingCell({ plan: planName, field });
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!editingCell) return;
    
    const { plan: planName, field } = editingCell;
    const plan = planName === 'free' ? freePlan : premiumPlan;
    if (!plan) return;

    // Validate
    if (field.includes('limit') || field.includes('maxAuditLogRetentionDays') || field === 'pricing.pricePerSeat') {
      const numValue = parseInt(editValue);
      if (isNaN(numValue) || numValue < -1) {
        setError(t('admin.subscriptionPlanConfig.limitsMustBePositive'));
        return;
      }
    }

    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      // Create update object
      const updatedPlan = JSON.parse(JSON.stringify(plan));
      setNestedValue(updatedPlan, field, field.includes('feature') ? editValue : 
                    (field.includes('limit') || field === 'pricing.pricePerSeat') ? parseInt(editValue) : editValue);

      const response = await fetch(`${apiUrl}/api/plan-config/${planName}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          displayName: updatedPlan.displayName,
          description: updatedPlan.description,
          limits: updatedPlan.limits,
          pricing: updatedPlan.pricing,
          features: updatedPlan.features
        })
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || t('admin.subscriptionPlanConfig.planConfigUpdateFailed'));
      }

      setSuccessMessage(t('admin.subscriptionPlanConfig.planConfigUpdated'));
      setTimeout(() => setSuccessMessage(null), 3000);
      
      cancelEdit();
      await fetchPlanConfigs();
      
    } catch (err) {
      console.error('Failed to update plan config:', err);
      setError(err instanceof Error ? err.message : t('admin.subscriptionPlanConfig.planConfigUpdateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const renderCellValue = (plan: PlanConfig | null, row: FeatureRow) => {
    if (!plan) return null;
    
    const value = getNestedValue(plan, row.field);
    const isEditing = editingCell?.plan === plan.planName && editingCell?.field === row.field;

    if (isEditing) {
      if (row.type === 'feature') {
        return (
          <div className="flex items-center gap-2">
            <select
              value={editValue ? 'true' : 'false'}
              onChange={(e) => setEditValue(e.target.value === 'true')}
              className="px-2 py-1 border border-primary-500 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-sm"
              autoFocus
            >
              <option value="true">✓ Enabled</option>
              <option value="false">✗ Disabled</option>
            </select>
            <button onClick={saveEdit} disabled={saving} className="p-1 text-success-600 hover:text-success-700">
              <Check className="w-4 h-4" />
            </button>
            <button onClick={cancelEdit} className="p-1 text-gray-600 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      } else if (row.field === 'pricing.billingInterval') {
        return (
          <div className="flex items-center gap-2">
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="px-2 py-1 border border-primary-500 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-sm"
              autoFocus
            >
              <option value="day">{t('admin.subscriptionPlanConfig.day')}</option>
              <option value="month">{t('admin.subscriptionPlanConfig.month')}</option>
              <option value="year">{t('admin.subscriptionPlanConfig.year')}</option>
            </select>
            <button onClick={saveEdit} disabled={saving} className="p-1 text-success-600 hover:text-success-700">
              <Check className="w-4 h-4" />
            </button>
            <button onClick={cancelEdit} className="p-1 text-gray-600 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      } else {
        return (
          <div className="flex items-center gap-2">
            <input
              type={row.type === 'limit' || row.type === 'pricing' ? 'number' : 'text'}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="px-2 py-1 border border-primary-500 rounded focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-sm w-full"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEdit();
                if (e.key === 'Escape') cancelEdit();
              }}
            />
            <button onClick={saveEdit} disabled={saving} className="p-1 text-success-600 hover:text-success-700 flex-shrink-0">
              <Check className="w-4 h-4" />
            </button>
            <button onClick={cancelEdit} className="p-1 text-gray-600 hover:text-gray-700 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      }
    }

    // Display mode
    if (row.type === 'feature') {
      return (
        <div 
          onClick={() => startEdit(plan.planName, row.field)}
          className="flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-2 py-1 transition-colors group"
        >
          {value ? (
            <CheckCircle2 className="w-5 h-5 text-success-600 dark:text-success-400" />
          ) : (
            <MinusCircle className="w-5 h-5 text-gray-400" />
          )}
          <Edit2 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      );
    } else if (row.type === 'limit') {
      const displayValue = value === -1 ? t('admin.subscriptionPlanConfig.unlimited') : value;
      return (
        <div 
          onClick={() => startEdit(plan.planName, row.field)}
          className="flex items-center justify-center gap-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-2 py-1 transition-colors group font-semibold"
        >
          <span className={value === -1 ? 'text-success-600 dark:text-success-400' : 'text-gray-900 dark:text-gray-100'}>
            {displayValue}
          </span>
          {row.suffix && value !== -1 && <span className="text-xs text-gray-500">{row.suffix}</span>}
          <Edit2 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
        </div>
      );
    } else if (row.type === 'pricing') {
      const displayValue = value === 0 ? 'Free' : `$${(value / 100).toFixed(2)}`;
      return (
        <div 
          onClick={() => startEdit(plan.planName, row.field)}
          className="flex items-center justify-center gap-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-2 py-1 transition-colors group font-semibold"
        >
          <span className="text-gray-900 dark:text-gray-100">{displayValue}</span>
          <Edit2 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
        </div>
      );
    } else {
      // text type
      return (
        <div 
          onClick={() => startEdit(plan.planName, row.field)}
          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-2 py-1 transition-colors group text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1"
        >
          <span>{value || '—'}</span>
          <Edit2 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
        </div>
      );
    }
  };

  if (!isSystemAdmin) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-warning-600 dark:text-warning-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-warning-900 dark:text-warning-200">
                {t('admin.subscriptionPlanConfig.systemAdminOnly')}
              </h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400">
            {t('admin.subscriptionPlanConfig.loadingPlanConfigs')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('admin.subscriptionPlanConfig.title')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          {t('admin.subscriptionPlanConfig.description')}
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-error-700 dark:bg-gray-700 dark:border dark:border-error-800/50 dark:border-red-800 dark:text-error-300 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-success-700 dark:bg-gray-700 dark:border dark:border-success-800/50 dark:border-green-800 dark:text-success-300 rounded-lg flex items-start gap-2">
          <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Comparison Matrix */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            {/* Header */}
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 w-1/3">
                  Feature
                </th>
                {freePlan && (
                  <th className="px-6 py-4 text-center w-1/3 border-l border-gray-200 dark:border-gray-600">
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-success-600 dark:text-success-400" />
                        <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {freePlan.displayName}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {freePlan.description}
                      </span>
                    </div>
                  </th>
                )}
                {premiumPlan && (
                  <th className="px-6 py-4 text-center w-1/3 border-l border-gray-200 dark:border-gray-600 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/10 dark:to-blue-900/10">
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-2">
                        <Zap className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {premiumPlan.displayName}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {premiumPlan.description}
                      </span>
                    </div>
                  </th>
                )}
                {/* Placeholder for future 3rd tier */}
                <th className="px-6 py-4 text-center w-1/3 border-l border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 opacity-50">
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-sm font-medium text-gray-400 dark:text-gray-600">
                      Enterprise
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-600">
                      Coming Soon
                    </span>
                  </div>
                </th>
              </tr>
            </thead>

            {/* Body */}
            <tbody>
              {featureRows.map((row, idx) => {
                const isCategory = !!row.category;
                const showCategory = isCategory || (idx > 0 && featureRows[idx - 1].category !== row.category);
                
                return (
                  <React.Fragment key={row.field}>
                    {/* Category Header */}
                    {showCategory && row.category && (
                      <tr className="bg-gray-100 dark:bg-gray-900">
                        <td colSpan={4} className="px-6 py-3 text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                          {row.category}
                        </td>
                      </tr>
                    )}
                    
                    {/* Feature Row */}
                    {!isCategory && (
                      <tr className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                          <div className="flex items-center gap-2">
                            {row.icon && <row.icon className="w-4 h-4 text-gray-400" />}
                            <span>{row.label}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center border-l border-gray-200 dark:border-gray-600">
                          {renderCellValue(freePlan, row)}
                        </td>
                        <td className="px-6 py-4 text-center border-l border-gray-200 dark:border-gray-600 bg-purple-50/30 dark:bg-purple-900/5">
                          {renderCellValue(premiumPlan, row)}
                        </td>
                        <td className="px-6 py-4 text-center border-l border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50">
                          <span className="text-gray-400 dark:text-gray-600 text-sm">—</span>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Help Text */}
      <div className="mt-4 p-4 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 border border-accent-200 dark:border-accent-800 rounded-lg">
        <p className="text-sm text-accent-900 dark:text-accent-200">
          💡 <strong>Tip:</strong> Click any value to edit. Use -1 for unlimited limits. Changes apply to new subscriptions immediately.
        </p>
      </div>
    </div>
  );
};
